import { useEffect, useState, useRef } from "react";
import { Button } from "@trillion/ui/components/ui/button";
import { ScrollArea } from "@trillion/ui/components/ui/scroll-area";
import { Badge } from "@trillion/ui/components/ui/badge";
import { Separator } from "@trillion/ui/components/ui/separator";
import { toast } from "sonner";
import { api } from "../lib/api";
import type { DiagramVersion } from "../types/board";

interface VersionHistoryPanelProps {
  diagramId: string;
  open: boolean;
  onClose: () => void;
  canRestore: boolean;
  historyBusy: boolean;
  getPreviewRequestKey: () => number;
  onRestore: (versionId: string) => Promise<boolean>;
  onPreview: (snapshot: unknown) => boolean;
  onExitPreview?: () => void;
  isPreviewing?: boolean;
}

export function VersionHistoryPanel({
  diagramId,
  open,
  onClose,
  onRestore,
  canRestore,
  historyBusy,
  getPreviewRequestKey,
  onPreview,
  onExitPreview,
  isPreviewing,
}: VersionHistoryPanelProps) {
  const [versions, setVersions] = useState<DiagramVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);

  const generation = useRef(0);
  const mounted = useRef(false);
  const restoring = useRef(false);
  const current = useRef({ diagramId, open, canRestore, historyBusy, onRestore, onPreview, getPreviewRequestKey });
  current.current = { diagramId, open, canRestore, historyBusy, onRestore, onPreview, getPreviewRequestKey };

  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    generation.current++;
    if (open) {
      setVersions([]);
      setPreviewingId(null);
      setIsLoading(true);
      api<{ versions: DiagramVersion[] }>(`/api/diagrams/${diagramId}/versions`)
        .then((res) => { if (!cancelled) setVersions(res.versions); })
        .catch(() => { if (!cancelled) toast.error("Failed to load version history"); })
        .finally(() => { if (!cancelled) setIsLoading(false); });
    }
    return () => {
      cancelled = true;
      mounted.current = false;
      generation.current++;
    };
  }, [open, diagramId]);

  useEffect(() => {
    if (!isPreviewing) setPreviewingId(null);
  }, [isPreviewing]);

  async function handlePreview(versionId: string) {
    if (!mounted.current || !current.current.open || current.current.diagramId !== diagramId ||
      current.current.historyBusy || restoring.current) return;
    const request = ++generation.current;
    const parentKey = current.current.getPreviewRequestKey();
    const isCurrent = () => mounted.current && current.current.open &&
      current.current.diagramId === diagramId && generation.current === request &&
      parentKey === current.current.getPreviewRequestKey() && !current.current.historyBusy && !restoring.current;
    try {
      const version = await api<{ version_id: string; snapshot: unknown }>(
        `/api/diagrams/${diagramId}/versions/${versionId}`
      );
      if (!isCurrent()) return;
      if (version.snapshot == null) {
        toast.error("This version has no snapshot data");
      } else if (current.current.onPreview(version.snapshot)) {
        setPreviewingId(versionId);
      }
    } catch {
      if (isCurrent()) toast.error("Failed to load version snapshot");
    }
  }

  function handleExitPreview() {
    generation.current++;
    if (current.current.historyBusy || restoring.current) return;
    setPreviewingId(null);
    onExitPreview?.();
  }

  async function handleRestore(versionId: string) {
    if (!mounted.current || !current.current.open || current.current.diagramId !== diagramId ||
      !current.current.canRestore || current.current.historyBusy || restoring.current) return;
    generation.current++;
    restoring.current = true;
    setRestoringId(versionId);
    try {
      if (await current.current.onRestore(versionId)) {
        if (mounted.current && current.current.diagramId === diagramId) setPreviewingId(null);
      }
    } finally {
      restoring.current = false;
      if (mounted.current && current.current.diagramId === diagramId) setRestoringId(null);
    }
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  const sourceBadgeVariant = (source: string) => {
    switch (source) {
      case "manual":
        return "default" as const;
      case "autosave":
        return "secondary" as const;
      case "restore":
        return "outline" as const;
      case "mcp":
        return "secondary" as const;
      default:
        return "outline" as const;
    }
  };

  if (!open) return null;

  return (
    <div className="board-side-panel flex h-full min-h-0 min-w-0 w-72 shrink-0 flex-col border-l bg-background">
      <div className="flex shrink-0 items-center justify-between border-b px-4 py-3">
        <h2 className="text-sm font-semibold">Version History</h2>
        <Button variant="ghost" size="sm" onClick={() => { generation.current++; onClose(); }}>
          Close
        </Button>
      </div>

      <ScrollArea className="min-h-0 min-w-0 flex-1">
        <div className="space-y-1 p-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading versions...</p>
          ) : versions.length === 0 ? (
            <p className="text-sm text-muted-foreground">No versions yet.</p>
          ) : (
            versions.map((version, index) => {
              const isActive = previewingId === version.version_id;
              return (
                <div key={version.version_id}>
                  <div
                    className={`rounded-lg border p-3 space-y-2 transition-colors ${
                      isActive
                        ? "border-primary bg-primary/5 ring-1 ring-primary"
                        : ""
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        {formatDate(version.created_date)}
                      </span>
                      <Badge variant={sourceBadgeVariant(version.source)}>
                        {version.source}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      by {version.display_name || version.created_by}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {isActive ? (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={historyBusy || restoringId !== null}
                            onClick={handleExitPreview}
                          >
                            Exit Preview
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            disabled={!canRestore || historyBusy || restoringId !== null}
                            onClick={() => handleRestore(version.version_id)}
                          >
                            {restoringId === version.version_id
                              ? "Restoring..."
                              : "Restore This"}
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={historyBusy || restoringId !== null}
                            onClick={() => handlePreview(version.version_id)}
                          >
                            Preview
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={!canRestore || historyBusy || restoringId !== null}
                            onClick={() => handleRestore(version.version_id)}
                          >
                            {restoringId === version.version_id
                              ? "Restoring..."
                              : "Restore"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {index < versions.length - 1 && <Separator className="my-2" />}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
